import SummarizeIcon from "@mui/icons-material/SummarizeOutlined";
import { Box, Divider, MenuList, Stack, Typography } from "@mui/material";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type {
  UploadWorkflowAttachmentResult,
  Workflow,
  WorkflowFileInfo,
} from "../../../../../api/types/workflowTypes";
import StepsProgress from "../../../../common/StepsProgress";
import WorkflowActivityTimelineIcon from "./WorkflowActivityTimelineIcon";
import { getActivityDisplayStatus, getStepTimeLabel, getWorkflowStepTitle } from "./workflowDetailsHelpers";
import WorkflowDocumentsSection from "./WorkflowDocumentsSection";
import WorkflowRunDetailsContext from "./WorkflowRunDetailsContext";
import WorkflowStepMenuItem from "./WorkflowStepMenuItem";

interface Props {
  onOpenAttachmentPreview: (attachment: WorkflowFileInfo) => void;
  onOpenEmailPreview: () => void;
  onFileUploaded: (result: UploadWorkflowAttachmentResult) => void;
  onFileDeleted: (updatedWorkflow: Workflow, fileCatalogId: string, fileName: string) => void;
  canManageFiles: boolean;
}

const WorkflowMasterPanel = ({
  onOpenAttachmentPreview,
  onOpenEmailPreview,
  onFileUploaded,
  onFileDeleted,
  canManageFiles,
}: Props) => {
  const { workflowDetails, steps, selectedStepIndex, selectStep } = useDefinedContext(
    WorkflowRunDetailsContext,
    "WorkflowRunDetailsContext"
  );

  const { workflow } = workflowDetails;

  const stepsCompleted =
    workflow.status === "Completed" ? workflow.activities.length : (workflow.currentActivityIndex ?? 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "25rem", flexShrink: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1.65 }}>
        <Typography variant="h6">Workflow</Typography>
        <StepsProgress completed={stepsCompleted} total={workflow.activities.length} width="50%" />
      </Box>
      <Divider />
      <WorkflowDocumentsSection
        workflowId={workflow.id}
        triggerInboxItem={workflowDetails.triggerInboxItem}
        documentFiles={workflow.documentFiles}
        onOpenAttachmentPreview={onOpenAttachmentPreview}
        onOpenEmailPreview={onOpenEmailPreview}
        onFileUploaded={onFileUploaded}
        onFileDeleted={onFileDeleted}
        canManageFiles={canManageFiles}
      />
      <Stack spacing={1} sx={{ flex: 1, px: 2, overflowX: "hidden", overflowY: "auto" }}>
        <Typography variant="subtitle2">Steps</Typography>
        <MenuList sx={{ flex: 1, overflowY: "auto", py: 0 }}>
          {steps.map((item, index) => {
            if (item.type === "activity") {
              const { activity } = item;
              const activityDisplayStatus = getActivityDisplayStatus(activity);
              return (
                <WorkflowStepMenuItem
                  // eslint-disable-next-line @eslint-react/no-array-index-key
                  key={activity.workflowActivityId ?? index}
                  icon={<WorkflowActivityTimelineIcon activity={activity} />}
                  title={getWorkflowStepTitle(item)}
                  subtitle={getStepTimeLabel(activity)}
                  selected={selectedStepIndex === index}
                  disabled={activityDisplayStatus === "NotStarted"}
                  pendingApproval={activityDisplayStatus === "PendingApproval"}
                  onClick={() => selectStep(index)}
                />
              );
            }

            if (item.type === "summary") {
              return (
                <WorkflowStepMenuItem
                  key="summary"
                  icon={<SummarizeIcon color="success" />}
                  title={getWorkflowStepTitle(item)}
                  selected={selectedStepIndex === index}
                  onClick={() => selectStep(index)}
                />
              );
            }

            return null;
          })}
        </MenuList>
      </Stack>
    </Box>
  );
};

export default WorkflowMasterPanel;
